import React, { useState } from 'react';
import { Shield, Scale, FileText, Fingerprint } from 'lucide-react';
import LegalModal from './LegalModal';
import { LEGAL_TEXTS } from '../../lib/legalTexts';

const LegalFooter: React.FC = () => {
    const [selectedLegal, setSelectedLegal] = useState<keyof typeof LEGAL_TEXTS | null>(null);

    return (
        <footer className="bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-900 pt-16 pb-32 md:pb-16 px-4">
            <LegalModal
                isOpen={!!selectedLegal}
                onClose={() => setSelectedLegal(null)}
                type={selectedLegal}
            />
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
                    {/* Brand & Disclosure */}
                    <div className="md:col-span-2 space-y-6">
                        <h3 className="text-xl font-black text-gray-950 dark:text-white uppercase tracking-tighter italic">
                            FastCash<span className="text-gray-400">Trade</span>
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed font-medium max-w-md">
                            FastCashTrade (FCT), 6563 sayılı Elektronik Ticaretin Düzenlenmesi Hakkında Kanun kapsamında <strong>Aracı Hizmet Sağlayıcı</strong> olarak faaliyet göstermektedir. Platformumuzda gerçekleştirilen işlemler "Sıfır Risk" güvencesi altındadır.
                        </p>

                        {/* ETBİS Placeholder ⚖️ */}
                        <div className="inline-flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 group hover:border-blue-500/20 transition-all cursor-pointer">
                            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-gray-100 overflow-hidden">
                                <span className="text-[10px] font-black leading-none text-center text-blue-600">ETBİS<br />KAYITLI</span>
                            </div>
                            <div>
                                <h4 className="text-[10px] font-black uppercase text-gray-950 dark:text-white tracking-widest">ETBİS Kayıtlı Site</h4>
                                <p className="text-[10px] text-gray-400 font-medium mt-0.5">Güvenli Alışveriş Sorgula</p>
                            </div>
                        </div>
                    </div>

                    {/* Legal Links 1 */}
                    <div className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase text-gray-400 dark:text-gray-500 tracking-[0.2em]">Yasal</h4>
                        <ul className="space-y-3">
                            <li
                                onClick={() => setSelectedLegal('MSS')}
                                className="flex items-center gap-2 group cursor-pointer"
                            >
                                <div className="w-6 h-6 rounded-lg bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                    <Scale size={14} />
                                </div>
                                <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 group-hover:text-gray-950 dark:group-hover:text-white transition-colors">Mesafeli Satış Sözleşmesi</span>
                            </li>
                            <li
                                onClick={() => setSelectedLegal('KVKK')}
                                className="flex items-center gap-2 group cursor-pointer"
                            >
                                <div className="w-6 h-6 rounded-lg bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                    <Fingerprint size={14} />
                                </div>
                                <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 group-hover:text-gray-950 dark:group-hover:text-white transition-colors">KVKK Aydınlatma Metni</span>
                            </li>
                            <li
                                onClick={() => setSelectedLegal('CEREZ')}
                                className="flex items-center gap-2 group cursor-pointer"
                            >
                                <div className="w-6 h-6 rounded-lg bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                    <Shield size={14} />
                                </div>
                                <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 group-hover:text-gray-950 dark:group-hover:text-white transition-colors">Çerez Politikası</span>
                            </li>
                        </ul>
                    </div>

                    {/* Legal Links 2 */}
                    <div className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase text-gray-400 dark:text-gray-500 tracking-[0.2em]">Sözleşmeler</h4>
                        <ul className="space-y-3">
                            <li
                                onClick={() => setSelectedLegal('KULLANIM')}
                                className="flex items-center gap-2 group cursor-pointer"
                            >
                                <div className="w-6 h-6 rounded-lg bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                    <FileText size={14} />
                                </div>
                                <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 group-hover:text-gray-950 dark:group-hover:text-white transition-colors">Kullanım Koşulları</span>
                            </li>
                            <li
                                onClick={() => setSelectedLegal('IADE')}
                                className="flex items-center gap-2 group cursor-pointer"
                            >
                                <div className="w-6 h-6 rounded-lg bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                    <FileText size={14} />
                                </div>
                                <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 group-hover:text-gray-950 dark:group-hover:text-white transition-colors">İptal ve İade Şartları</span>
                            </li>
                            <li
                                onClick={() => setSelectedLegal('MASAK')}
                                className="flex items-center gap-2 group cursor-pointer"
                            >
                                <div className="w-6 h-6 rounded-lg bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                    <Shield size={14} />
                                </div>
                                <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 group-hover:text-gray-950 dark:group-hover:text-white transition-colors">MASAK Uyum Politikası</span>
                            </li>
                            <li
                                onClick={() => setSelectedLegal('HAKEDIS')}
                                className="flex items-center gap-2 group cursor-pointer"
                            >
                                <div className="w-6 h-6 rounded-lg bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                    <FileText size={14} />
                                </div>
                                <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 group-hover:text-gray-950 dark:group-hover:text-white transition-colors">Hakediş ve Blokaj</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="pt-8 border-t border-gray-100 dark:border-gray-900 flex flex-col md:flex-row justify-between items-center gap-8">
                    <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest order-2 md:order-1">
                        © 2024 FastCashTrade. Tüm Hakları Saklıdır.
                    </p>
                    <div className="flex items-center gap-3 order-1 md:order-2">
                        {/* Premium Payment Icons */}
                        <div className="flex items-center gap-2 px-4 py-2 bg-gray-50/50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5 backdrop-blur-sm shadow-sm hover:shadow-md transition-all group">
                            <div className="flex -space-x-1">
                                <div className="w-10 h-6 bg-white dark:bg-gray-800 rounded-md border border-gray-100 dark:border-gray-700 p-1 flex items-center justify-center shadow-sm group-hover:-translate-y-0.5 transition-transform duration-300">
                                    <span className="text-[8px] font-black text-[#1A1F71] dark:text-[#5d67ff] italic tracking-tighter">VISA</span>
                                </div>
                                <div className="w-10 h-6 bg-white dark:bg-gray-800 rounded-md border border-gray-100 dark:border-gray-700 p-1 flex items-center justify-center shadow-sm group-hover:-translate-y-1 transition-transform duration-300">
                                    <div className="flex -space-x-1.5">
                                        <div className="w-3 h-3 bg-[#EB001B] rounded-full opacity-90" />
                                        <div className="w-3 h-3 bg-[#F79E1B] rounded-full opacity-90" />
                                    </div>
                                </div>
                                <div className="w-10 h-6 bg-white dark:bg-gray-800 rounded-md border border-gray-100 dark:border-gray-700 p-1 flex items-center justify-center shadow-sm group-hover:-translate-y-0.5 transition-transform duration-300">
                                    <span className="text-[8px] font-black text-gray-900 dark:text-white tracking-widest opacity-80 uppercase">Troy</span>
                                </div>
                            </div>
                            <div className="w-px h-3 bg-gray-200 dark:bg-gray-800 mx-1" />
                            <div className="flex items-center gap-1.5">
                                <Shield size={10} className="text-emerald-500" />
                                <span className="text-[8px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">PCI-DSS Secure</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default LegalFooter;
